/**
 * UX-07 banner — "Couldn't reach Salesforce". Renders when:
 *   - the API returned 502 (Fastify maps jsforce CONNECTION_TIMEOUT etc.)
 *   - OR the error body included `code: 'SF_UNREACHABLE'`
 *
 * Sits inline with the tile (not page-level) so the surrounding chrome
 * (header, nav, freshness pill on other tiles) stays interactive.
 *
 * Carried forward verbatim from packages/spa/src/components/SfUnreachableBanner.tsx.
 */
import { Button } from './ui/Button.js';

interface SfUnreachableBannerProps {
  onRetry?: () => void;
  onDismiss?: () => void;
}

export function SfUnreachableBanner({
  onRetry,
  onDismiss,
}: SfUnreachableBannerProps): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-bad bg-bad-bg px-4 py-3 text-sm text-bad-text"
      data-testid="sf-unreachable-banner"
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-bad text-xs font-bold text-white"
      >
        !
      </span>
      <div className="flex-1">
        <p className="font-semibold">Couldn&apos;t reach Salesforce — try again in a moment.</p>
        <p className="mt-1 text-xs text-bad-text/80">
          The dashboard preserved your session; refreshing this tile will retry the live query.
        </p>
      </div>
      <div className="flex flex-none items-center gap-2">
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss banner">
            ×
          </Button>
        )}
      </div>
    </div>
  );
}
